import { useEffect, useRef, useState } from "react";
import { 
  ArrowRight, 
  BookOpen, 
  Brain, 
  Calendar, 
  Clock, 
  Compass, 
  Heart, 
  LucideIcon, 
  Users, 
  QrCode,
  Feather,
  Leaf,
  Library,
  Map,
  Mountain,
  Home,
  Sparkles
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export default function ProgramOverview() {
  const [elementVisibility, setElementVisibility] = useState({
    journey: false,
    path: false,
    elements: false,
    outcomes: false,
    eligibility: false,
    guide: false,
    application: false,
  });

  const journeyRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<HTMLDivElement>(null);
  const outcomesRef = useRef<HTMLDivElement>(null);
  const eligibilityRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const applicationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setElementVisibility(prev => ({
            ...prev,
            [sectionId]: true
          }));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (journeyRef.current) observer.observe(journeyRef.current);
    if (pathRef.current) observer.observe(pathRef.current);
    if (elementsRef.current) observer.observe(elementsRef.current);
    if (outcomesRef.current) observer.observe(outcomesRef.current);
    if (eligibilityRef.current) observer.observe(eligibilityRef.current);
    if (guideRef.current) observer.observe(guideRef.current);
    if (applicationRef.current) observer.observe(applicationRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white print:bg-white">
      <div className="container mx-auto px-6 py-12 max-w-5xl print:py-8">
        <div className="flex flex-col items-center text-center mb-24 animate-fade-in-slow">
          <img 
            src="/lovable-uploads/0788554f-dbfe-44f8-bd67-7e7e91abbb3c.png" 
            alt="The Monastery Method™" 
            className="w-44 h-44 object-contain mb-6"
          />
          <h2 className="text-2xl md:text-3xl font-serif text-charcoal/90 relative">
            <span className="relative inline-block">
              Ancient Wisdom for Modern Leadership
              <span className="absolute -bottom-2 left-1/4 right-1/4 h-px bg-charcoal/20"></span>
            </span>
          </h2>
        </div>

        <div 
          ref={journeyRef} 
          id="journey" 
          className={`opacity-0 transition-all duration-1000 ease-out ${
            elementVisibility.journey ? 'opacity-100 translate-y-0' : 'translate-y-10'
          }`}
        >
          <SectionWithIcon 
            title="THE JOURNEY" 
            icon={Compass}
            content={
              <div className="p-8 bg-gradient-to-b from-offwhite to-white rounded-sm shadow-sm border border-charcoal/5">
                <p className="text-lg text-center text-charcoal/90 max-w-2xl mx-auto leading-relaxed">
                  The Monastery Method™ guides participants through a transformative 7-week journey from the chaos of modern life to the freedom of authentic living. This daily, hands-on experience moves through three sacred stages: departing from noise, encountering wisdom in the desert, and returning with renewed clarity, purpose, and sustainable peace—without leaving your place in the world.
                </p>
              </div>
            }
          />
        </div>

        <div 
          ref={pathRef} 
          id="path" 
          className={`opacity-0 transition-all duration-1000 ease-out ${
            elementVisibility.path ? 'opacity-100 translate-y-0' : 'translate-y-10'
          }`}
        >
          <SectionWithIcon title="YOUR PATH" icon={BookOpen}>
            <div className="grid md:grid-cols-2 gap-8">
              <PathStage 
                week="WEEK 1" 
                title="LEAVING THE METROPOLIS" 
                quote="The most dangerous form of success is the kind that fills your bank account while emptying your soul."
                points={[
                  "Examine the true nature of freedom",
                  "Break free from mimetic desire",
                  "Begin the simple practice of morning silence",
                  "Discover the way to your true identity"
                ]}
                color="bg-cream/20"
                delay={0}
              />
              
              <PathStage 
                week="WEEKS 2-4" 
                title="THE DESERT EXPERIENCE" 
                quote="What got you here won't get you there."
                points={[
                  "Discover freedom from compulsive desires",
                  "Release the addiction to recognition",
                  "Let go of the illusion of control",
                  "Establish transformative daily practices"
                ]}
                color="bg-cream/30"
                delay={150}
              />
              
              <PathStage 
                week="WEEKS 5-6" 
                title="THE MONASTERY" 
                quote="Peace is not something to pursue directly, but an outcome of living differently."
                points={[
                  "Transform your closest relationships",
                  "Bring renewed presence to your work",
                  "Develop the art of sacred conversation",
                  "Experience the joy of authentic connection"
                ]}
                color="bg-cream/40"
                delay={300}
              />
              
              <PathStage 
                week="WEEK 7" 
                title="RETURN TO THE WORLD" 
                quote="True success isn't about what you achieve. It's about who you become."
                points={[
                  "Integrate all practices into daily life",
                  "Establish your personalized rule of life",
                  "Live from a place of authentic freedom",
                  "Experience sustainable peace amid achievement"
                ]}
                color="bg-cream/50"
                delay={450}
              />
            </div>
          </SectionWithIcon>
        </div>

        <div 
          ref={elementsRef} 
          id="elements" 
          className={`opacity-0 transition-all duration-1000 ease-out ${
            elementVisibility.elements ? 'opacity-100 translate-y-0' : 'translate-y-10'
          }`}
        >
          <SectionWithIcon title="PROGRAM ELEMENTS" icon={Clock}>
            <div className="grid md:grid-cols-3 gap-8">
              <ElementCard
                title="DAILY RHYTHM"
                icon={Calendar}
                points={[
                  "Morning practice (10 minutes)",
                  "Midday renewal (10 minutes)",
                  "Evening integration (10 minutes)",
                  "Simple, sustainable practices"
                ]}
                delay={0}
              />
              
              <ElementCard
                title="WEEKLY GATHERING"
                icon={Users}
                points={[
                  "Small group connection",
                  "Shared experience",
                  "Personal guidance",
                  "Community support"
                ]}
                delay={150}
              />
              
              <ElementCard
                title="TRANSFORMATIVE PRACTICES"
                icon={Brain}
                points={[
                  "Sacred pause",
                  "Presence cultivation",
                  "Pattern recognition",
                  "Paradox integration",
                  "Purpose alignment"
                ]}
                delay={300}
              />
            </div>
          </SectionWithIcon>
        </div>

        <div 
          ref={outcomesRef} 
          id="outcomes" 
          className={`opacity-0 transition-all duration-1000 ease-out ${
            elementVisibility.outcomes ? 'opacity-100 translate-y-0' : 'translate-y-10'
          }`}
        >
          <SectionWithIcon title="THE OUTCOMES" icon={Heart}>
            <div className="bg-gradient-to-b from-offwhite to-white p-8 rounded-sm shadow-sm border border-charcoal/5">
              <p className="text-lg mb-8 text-center">Participants emerge from The Monastery Method™ with:</p>
              
              <ul className="space-y-4 max-w-2xl mx-auto">
                <OutcomeItem 
                  title="Mental clarity" 
                  description="that cuts through noise and distraction"
                  icon={Brain}
                  delay={0}
                />
                <OutcomeItem 
                  title="Emotional freedom" 
                  description="from the tyranny of comparison"
                  icon={Feather}
                  delay={150}
                />
                <OutcomeItem 
                  title="Spiritual depth" 
                  description="that transcends circumstance"
                  icon={Leaf}
                  delay={300}
                />
                <OutcomeItem 
                  title="Relational authenticity" 
                  description="in family and professional life"
                  icon={Users}
                  delay={450}
                />
                <OutcomeItem 
                  title="Purposeful direction" 
                  description="aligned with deepest values"
                  icon={Compass}
                  delay={600}
                />
              </ul>
            </div>
          </SectionWithIcon>
        </div>

        <div 
          ref={eligibilityRef} 
          id="eligibility" 
          className={`opacity-0 transition-all duration-1000 ease-out ${
            elementVisibility.eligibility ? 'opacity-100 translate-y-0' : 'translate-y-10'
          }`}
        >
          <SectionWithIcon title="ELIGIBILITY REQUIREMENTS" icon={Users}>
            <div className="bg-gradient-to-b from-cream/20 to-white p-8 rounded-sm shadow-sm border border-charcoal/5">
              <p className="text-lg mb-8 text-center max-w-2xl mx-auto leading-relaxed font-serif italic text-charcoal/90">
                <strong>
                  This edition of the Monastery Method isn't for everyone.
                </strong>
                <br />
                It's for accomplished leaders seeking deeper integration of success and meaning.
                <br /><br />
                <span className="font-sans not-italic">This program is for you if:</span>
              </p>
              <ul className="space-y-5 max-w-xl mx-auto">
                <EligibilityItem 
                  text="You're a leader with significant responsibility (executive, business owner, or high-impact professional)."
                  delay={0}
                />
                <EligibilityItem 
                  text="You've achieved recognized success in your field for at least five years."
                  delay={150}
                />
                <EligibilityItem 
                  text="You sense that greater achievement alone won't bring the fulfillment you seek."
                  delay={300}
                />
                <EligibilityItem 
                  text="You're ready to invest in transforming your relationship with success, meaning, and purpose."
                  delay={450}
                />
              </ul>
            </div>
          </SectionWithIcon>
        </div>

        <div 
          ref={guideRef} 
          id="guide" 
          className={`opacity-0 transition-all duration-1000 ease-out ${
            elementVisibility.guide ? 'opacity-100 translate-y-0' : 'translate-y-10'
          }`}
        >
          <SectionWithIcon title="YOUR GUIDE" icon={Compass}>
            <div className="flex flex-col md:flex-row items-center gap-10 max-w-3xl mx-auto bg-gradient-to-b from-gray-50 to-white p-8 rounded-sm shadow-sm border border-charcoal/5 overflow-hidden">
              <div className="relative w-52 h-52 flex-shrink-0 animate-fade-in-slow">
                <div className="absolute inset-0 bg-cream/20 rounded-full transform animate-pulse opacity-70" style={{ animationDuration: '3s' }}></div>
                <Avatar className="w-52 h-52 border-4 border-cream/40 shadow-md transition-all duration-500 hover:border-cream/70 hover:shadow-lg">
                  <AvatarImage 
                    src="/lovable-uploads/20b29789-0c27-4ca5-bc2c-8738168184c4.png" 
                    alt="Tommy Zee" 
                    className="object-cover"
                  />
                  <AvatarFallback className="text-4xl bg-cream/40">TZ</AvatarFallback>
                </Avatar>
              </div>
              <p className="text-charcoal/90 leading-relaxed text-lg relative z-10 animate-fade-in-slow" style={{ animationDelay: '300ms' }}>
                Drawing from 20+ years working with global brands across three continents while studying timeless wisdom traditions, <span className="font-medium">Tommy Zee</span> has integrated ancient monastic principles with modern leadership for a unique approach to sustainable success with meaning.
              </p>
            </div>
          </SectionWithIcon>
        </div>

        <div 
          ref={applicationRef} 
          id="application" 
          className={`opacity-0 transition-all duration-1000 ease-out ${
            elementVisibility.application ? 'opacity-100 translate-y-0' : 'translate-y-10'
          }`}
        >
          <section className="mb-12 mt-20">
            <div className="text-center">
              <div className="mb-8">
                <h2 className="text-2xl font-serif mb-2 tracking-wide">
                  <span>Limited to 12 Leaders for Founding Cohort</span>
                </h2>
                <p className="text-sm uppercase tracking-wider text-charcoal/60 mt-2 animate-pulse">
                  Selection Conversations Currently Underway
                </p>
                <div className="h-px w-24 bg-charcoal/20 mx-auto mt-4"></div>
              </div>
              
              <div className="mb-10 max-w-2xl mx-auto transform transition-all duration-700">
                <div className="py-8 px-6 md:px-12 bg-cream/10 border border-charcoal/10 rounded-sm">
                  <p className="text-2xl font-serif italic text-charcoal/90 relative py-6 px-4">
                    <span className="absolute top-0 left-0 text-6xl text-cream/80 opacity-50 font-serif" style={{ marginTop: '-0.5rem', marginLeft: '-0.5rem' }}>"</span>
                    <span className="relative z-10 leading-relaxed">For what will it profit a man if he gains the whole world, but loses his soul?</span>
                    <span className="absolute bottom-0 right-0 text-6xl text-cream/80 opacity-50 font-serif" style={{ marginBottom: '-1.5rem', marginRight: '-0.5rem' }}>"</span>
                  </p>
                  <div className="mt-6 text-right text-charcoal/70 text-sm italic">
                    — Mark 8:36
                  </div>
                </div>
                <div className="h-px w-36 bg-charcoal/20 mx-auto mt-6"></div>
              </div>
              
              <div className="print:hidden">
                <a 
                  href="https://tommyzee.typeform.com/monasterymethod" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-charcoal hover:bg-charcoal/90 text-white px-10 py-3 text-sm uppercase tracking-widest transition-all duration-300 shadow-sm group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center">
                    Start the Conversation 
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <span className="absolute inset-0 bg-charcoal/90 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></span>
                </a>
              </div>
              
              <div className="hidden print:block">
                <div className="border-2 border-charcoal/10 p-6 mx-auto max-w-md rounded-sm shadow-sm">
                  <h3 className="text-xl font-serif mb-4 flex items-center justify-center">
                    <QrCode className="h-5 w-5 mr-2 text-charcoal/70" />
                    HOW TO APPLY
                  </h3>
                  
                  <p className="mb-4 text-charcoal/90">
                    To submit your application for The Monastery Method™, visit:
                  </p>
                  
                  <p className="text-xl font-medium mb-4 border-b border-t py-3 border-charcoal/10">
                    tommyzee.typeform.com/monasterymethod
                  </p>
                  
                  <p className="text-sm text-charcoal/70">
                    Applications are reviewed within 48 hours. Questions can be directed to knock@themonasterymethod.com
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <footer className="hidden print:block text-center mt-16 pt-8 border-t border-charcoal/10">
          <p className="text-charcoal/60 text-sm">
            © The Monastery Method™ | themonasterymethod.com
          </p>
        </footer>
      </div>
    </div>
  );
}

type SectionWithIconProps = {
  title: string;
  icon: LucideIcon;
  content?: React.ReactNode;
  children?: React.ReactNode;
};

const SectionWithIcon = ({ title, icon: Icon, content, children }: SectionWithIconProps) => {
  return (
    <section className="mb-24 print:mb-16">
      <h3 className="text-2xl font-serif mb-12 text-center tracking-wide flex items-center justify-center relative">
        <span className="absolute h-px w-12 bg-charcoal/20 left-1/2 -translate-x-[calc(50%+40px)]"></span>
        <Icon className="h-6 w-6 mx-3 text-charcoal/80" />
        <span className="relative">
          {title}
          <span className="absolute h-px w-12 bg-charcoal/20 right-0 top-1/2 translate-x-[calc(100%+12px)]"></span>
        </span>
      </h3>
      {content}
      {children}
    </section>
  );
};

type PathStageProps = {
  week: string;
  title: string;
  quote: string;
  points: string[];
  color: string;
  delay: number;
};

const PathStage = ({ week, title, quote, points, color, delay }: PathStageProps) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const getIconForStage = (title: string) => {
    switch (title) {
      case "LEAVING THE METROPOLIS":
        return <Compass className="h-5 w-5 text-charcoal/70" />;
      case "THE DESERT EXPERIENCE":
        return <Mountain className="h-5 w-5 text-charcoal/70" />;
      case "THE MONASTERY":
        return <Library className="h-5 w-5 text-charcoal/70" />;
      case "RETURN TO THE WORLD":
        return <Home className="h-5 w-5 text-charcoal/70" />;
      default:
        return null;
    }
  };

  return (
    <div 
      className={`p-6 rounded-sm shadow-sm border border-charcoal/5 transition-all duration-300 ${color} ${
        isHovered ? 'transform -translate-y-1 shadow-md' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        animationDelay: `${delay}ms`,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
      }}
    >
      <div className="text-center mb-5">
        <p className="text-sm font-medium text-charcoal/70 uppercase tracking-wider">{week}</p>
        <h4 className="text-xl font-serif mb-4 flex items-center justify-center gap-2">
          {getIconForStage(title)}
          <span>{title}</span>
        </h4>
        <p className="text-base italic text-charcoal/80 mb-5 font-serif">
          "{quote}"
        </p>
      </div>
      <ul className="space-y-2 text-charcoal/90">
        {points.map((point, idx) => (
          <li key={idx} className="flex items-start">
            <span className="h-1 w-1 rounded-full bg-charcoal/50 mt-2 mr-2 flex-shrink-0"></span>
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

type ElementCardProps = {
  title: string;
  icon: LucideIcon;
  points: string[];
  delay: number;
};

const ElementCard = ({ title, icon: Icon, points, delay }: ElementCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className={`bg-gradient-to-b from-gray-50 to-white p-8 rounded-sm shadow-sm border border-charcoal/5 transition-all duration-300 ${
        isHovered ? 'transform -translate-y-1 shadow-md' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        animationDelay: `${delay}ms`,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
      }}
    >
      <h4 className="text-xl font-serif mb-5 flex items-center justify-center">
        <span className={`p-2 rounded-full bg-cream/50 mr-3 transition-all duration-300 ${
          isHovered ? 'bg-cream/80' : ''
        }`}>
          <Icon className="h-5 w-5 text-charcoal/70" />
        </span>
        {title}
      </h4>
      <ul className="space-y-3 text-charcoal/90">
        {points.map((point, idx) => (
          <li key={idx} className="flex items-center">
            <span className="h-1 w-1 rounded-full bg-charcoal/50 mr-2 flex-shrink-0"></span>
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

type OutcomeItemProps = {
  title: string;
  description: string;
  icon: LucideIcon;
  delay: number;
};

const OutcomeItem = ({ title, description, icon: Icon, delay }: OutcomeItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <li 
      className={`flex items-center transition-all duration-300 ${
        isHovered ? 'transform translate-x-1' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        animationDelay: `${delay}ms`,
        transition: 'transform 0.3s ease'
      }}
    >
      <span className={`p-1 rounded-full bg-cream/30 mr-3 transition-all duration-300 ${
        isHovered ? 'bg-cream/60' : ''
      }`}>
        <Icon className="h-4 w-4 text-charcoal/70" />
      </span>
      <span className="font-medium">{title}</span>
      <span className="mx-2 text-charcoal/70">—</span>
      <span className="text-charcoal/90">{description}</span>
    </li>
  );
};

type EligibilityItemProps = {
  text: string;
  delay: number;
};

const EligibilityItem = ({ text, delay }: EligibilityItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <li 
      className={`flex items-start transition-all duration-300 ${
        isHovered ? 'transform translate-x-1' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        animationDelay: `${delay}ms`,
        transition: 'transform 0.3s ease'
      }}
    >
      <span className={`h-2.5 w-2.5 rounded-full bg-charcoal/40 mt-2 mr-4 transition-all duration-300 ${
        isHovered ? 'bg-charcoal/70' : ''
      }`}></span>
      <span className="text-charcoal/90 text-lg">{text}</span>
    </li>
  );
};
